import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { MetalService } from "@/gen/omnios/v1/metal_connect";
import { MetalPriceUnit } from "@/gen/omnios/v1/metal_pb";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/lib/workspace";
import { createClient } from "@connectrpc/connect";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
	ArrowRightLeft,
	Calculator,
	Check,
	Loader2,
	Plus,
	Scissors,
	Search,
	Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/settings/metal")({
	beforeLoad: () => {
		const ws = useWorkspaceStore.getState().selected();
		if (!ws?.features.includes("metal")) {
			throw redirect({ to: "/settings" });
		}
	},
	component: MetalSettingsPage,
});

const client = createClient(MetalService, transport);

// ── Shared constants ─────────────────────────────────────────────

type TabId = "cutting" | "sortament" | "calculator" | "converter";

function useShapeOptions() {
	const { t } = useTranslation("settings");
	return [
		{ value: "_all", label: t("metal_shape_all") },
		{ value: "round_bar", label: t("metal_shape_round_bar") },
		{ value: "square_bar", label: t("metal_shape_square_bar") },
		{ value: "flat_bar", label: t("metal_shape_flat_bar") },
		{ value: "hex_bar", label: t("metal_shape_hex_bar") },
		{ value: "round_pipe", label: t("metal_shape_round_pipe") },
		{ value: "square_pipe", label: t("metal_shape_square_pipe") },
		{ value: "rect_pipe", label: t("metal_shape_rect_pipe") },
		{ value: "sheet", label: t("metal_shape_sheet") },
		{ value: "rebar", label: t("metal_shape_rebar") },
		{ value: "wire", label: t("metal_shape_wire") },
		{ value: "l_angle", label: t("metal_shape_l_angle") },
		{ value: "channel", label: t("metal_shape_channel") },
		{ value: "i_beam", label: t("metal_shape_i_beam") },
		{ value: "t_profile", label: t("metal_shape_t_profile") },
		{ value: "wire_mesh", label: t("metal_shape_wire_mesh") },
	];
}

function useShapeDims() {
	const { t } = useTranslation("settings");
	return {
		"1": [{ key: "diameter", label: t("metal_dim_diameter") }],
		"2": [{ key: "side", label: t("metal_dim_side") }],
		"3": [
			{ key: "width", label: t("metal_dim_width") },
			{ key: "thickness", label: t("metal_dim_thickness") },
		],
		"4": [{ key: "across_flats", label: t("metal_dim_across_flats") }],
		"5": [
			{ key: "outer_diameter", label: t("metal_dim_outer_diameter") },
			{ key: "wall_thickness", label: t("metal_dim_wall_thickness") },
		],
		"6": [
			{ key: "side", label: t("metal_dim_side") },
			{ key: "wall_thickness", label: t("metal_dim_wall_thickness") },
		],
		"7": [
			{ key: "side_a", label: t("metal_dim_side_a") },
			{ key: "side_b", label: t("metal_dim_side_b") },
			{ key: "wall_thickness", label: t("metal_dim_wall_thickness") },
		],
		"8": [
			{ key: "width", label: t("metal_dim_width") },
			{ key: "length", label: t("metal_dim_length") },
			{ key: "thickness", label: t("metal_dim_thickness") },
		],
		"9": [{ key: "diameter", label: t("metal_dim_diameter") }],
		"10": [{ key: "diameter", label: t("metal_dim_diameter") }],
		"11": [
			{ key: "height", label: t("metal_dim_shelf_a") },
			{ key: "width", label: t("metal_dim_shelf_b") },
			{ key: "thickness", label: t("metal_dim_thickness") },
		],
		"12": [
			{ key: "height", label: t("metal_dim_height") },
			{ key: "flange_width", label: t("metal_dim_flange_width") },
			{ key: "web_thickness", label: t("metal_dim_web_thickness") },
			{ key: "flange_thickness", label: t("metal_dim_flange_thickness") },
		],
		"13": [
			{ key: "height", label: t("metal_dim_height") },
			{ key: "top_flange_width", label: t("metal_dim_top_flange_width") },
			{ key: "top_flange_thickness", label: t("metal_dim_top_flange_thick") },
			{ key: "bottom_flange_width", label: t("metal_dim_bottom_flange_width") },
			{
				key: "bottom_flange_thickness",
				label: t("metal_dim_bottom_flange_thick"),
			},
			{ key: "web_thickness", label: t("metal_dim_web_thickness") },
		],
		"14": [
			{ key: "height", label: t("metal_dim_height") },
			{ key: "flange_width", label: t("metal_dim_flange_width") },
			{ key: "flange_thickness", label: t("metal_dim_flange_thickness") },
			{ key: "stem_thickness", label: t("metal_dim_stem_thickness") },
		],
		"15": [
			{ key: "wire_diameter", label: t("metal_dim_wire_diameter") },
			{ key: "cell_size", label: t("metal_dim_cell_size") },
			{ key: "sheet_width", label: t("metal_dim_sheet_width") },
			{ key: "sheet_length", label: t("metal_dim_sheet_length") },
		],
	} as Record<string, { key: string; label: string }[]>;
}

function useCalcShapes() {
	const { t } = useTranslation("settings");
	return [
		{ value: "9", label: t("metal_shape_rebar") },
		{ value: "1", label: t("metal_shape_round_bar") },
		{ value: "2", label: t("metal_shape_square_bar") },
		{ value: "3", label: t("metal_shape_flat_bar") },
		{ value: "4", label: t("metal_shape_hex_bar") },
		{ value: "5", label: t("metal_shape_round_pipe") },
		{ value: "6", label: t("metal_shape_square_pipe") },
		{ value: "7", label: t("metal_shape_rect_pipe") },
		{ value: "8", label: t("metal_shape_sheet") },
		{ value: "10", label: t("metal_shape_wire") },
		{ value: "11", label: t("metal_shape_l_angle") },
		{ value: "12", label: t("metal_shape_channel") },
		{ value: "13", label: t("metal_shape_i_beam") },
		{ value: "14", label: t("metal_shape_t_profile") },
		{ value: "15", label: t("metal_shape_wire_mesh") },
	];
}

function useMetals() {
	const { t } = useTranslation("settings");
	return [
		{ value: "steel", label: t("metal_metal_steel"), density: "7,85" },
		{
			value: "low_alloy_steel",
			label: t("metal_metal_low_alloy"),
			density: "7,85",
		},
		{
			value: "stainless_austenitic",
			label: t("metal_metal_stainless_aust"),
			density: "7,90",
		},
		{
			value: "stainless_ferritic",
			label: t("metal_metal_stainless_ferr"),
			density: "7,75",
		},
		{ value: "aluminium", label: t("metal_metal_aluminium"), density: "2,70" },
		{ value: "copper", label: t("metal_metal_copper"), density: "8,94" },
		{ value: "brass_l63", label: t("metal_metal_brass"), density: "8,44" },
		{
			value: "titanium_vt1",
			label: t("metal_metal_titanium"),
			density: "4,51",
		},
	];
}

// ── Autosave indicator (shared component) ────────────────────────

function SaveIndicator({ saving, saved }: { saving: boolean; saved: boolean }) {
	const { t } = useTranslation("settings");
	if (!saving && !saved) return <div className="h-5" />;
	return (
		<div className="h-5">
			{saving && (
				<span className="flex items-center gap-1.5 text-xs text-muted-foreground">
					<Loader2 className="h-3.5 w-3.5 animate-spin" />
					{t("metal_saving")}
				</span>
			)}
			{saved && !saving && (
				<span className="flex items-center gap-1.5 text-xs text-green-600">
					<Check className="h-3.5 w-3.5" />
					{t("metal_saved")}
				</span>
			)}
		</div>
	);
}

// ── Page ─────────────────────────────────────────────────────────

function MetalSettingsPage() {
	const { t } = useTranslation("settings");

	const TABS = [
		{ id: "cutting" as const, label: t("metal_tab_cutting"), icon: Scissors },
		{ id: "sortament" as const, label: t("metal_tab_sortament"), icon: Search },
		{
			id: "calculator" as const,
			label: t("metal_tab_calculator"),
			icon: Calculator,
		},
		{
			id: "converter" as const,
			label: t("metal_tab_converter"),
			icon: ArrowRightLeft,
		},
	];

	useBreadcrumbs([
		{ label: t("breadcrumb_settings"), href: "/settings" },
		{ label: t("metal_breadcrumb") },
	]);

	const [activeTab, setActiveTab] = useState<TabId>("cutting");

	return (
		<div>
			<div className="-mx-6 -mt-4 flex border-b bg-muted/30">
				{TABS.map((tab) => (
					<Button
						key={tab.id}
						variant="ghost"
						size="sm"
						onClick={() => setActiveTab(tab.id)}
						className={cn(
							"gap-1.5 px-5 py-2.5 rounded-none border-b-2 -mb-px",
							activeTab === tab.id
								? "border-primary text-foreground bg-background"
								: "border-transparent text-muted-foreground hover:text-foreground",
						)}
					>
						<tab.icon className="h-3.5 w-3.5" />
						{tab.label}
					</Button>
				))}
			</div>

			<div className="pt-5">
				{activeTab === "cutting" && <CuttingRatesTab />}
				{activeTab === "sortament" && <SortamentTab />}
				{activeTab === "calculator" && <CalculatorTab />}
				{activeTab === "converter" && <PriceConverterTab />}
			</div>
		</div>
	);
}

// ── Cutting rates (autosave) ─────────────────────────────────────

interface RateDraft {
	key: string;
	shape: string;
	ratePerCutRub: string;
	ratePerMetreRub: string;
	kerfMm: string;
}

function CuttingRatesTab() {
	const { t } = useTranslation("settings");
	const SHAPE_OPTIONS = useShapeOptions();
	const qc = useQueryClient();
	const { data, isLoading } = useQuery({
		queryKey: ["metal", "cutting-rates"],
		queryFn: () => client.getCuttingRates({}),
	});
	const [drafts, setDrafts] = useState<RateDraft[]>([]);
	const [initialized, setInitialized] = useState(false);
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saved, setSaved] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

	if (data && !initialized) {
		setDrafts(
			data.rates.length > 0
				? data.rates.map((r) => ({
						key: r.id || crypto.randomUUID(),
						shape: r.shape || "_all",
						ratePerCutRub: (Number(r.ratePerCutCents) / 100).toString(),
						ratePerMetreRub: (Number(r.ratePerMetreCents) / 100).toString(),
						kerfMm: r.kerfMm.toString(),
					}))
				: [],
		);
		setInitialized(true);
	}

	// Autosave with 1.2s debounce
	useEffect(() => {
		if (!dirty || !initialized) return;
		if (timer.current) clearTimeout(timer.current);
		timer.current = setTimeout(async () => {
			setSaving(true);
			setSaved(false);
			try {
				await client.setCuttingRates({
					rates: drafts.map((d) => ({
						shape: d.shape === "_all" ? "" : d.shape,
						ratePerCutCents: BigInt(
							Math.round(Number.parseFloat(d.ratePerCutRub || "0") * 100),
						),
						ratePerMetreCents: BigInt(
							Math.round(Number.parseFloat(d.ratePerMetreRub || "0") * 100),
						),
						kerfMm: Number.parseFloat(d.kerfMm || "3"),
						currency: "RUB",
						isActive: true,
					})),
				});
				qc.invalidateQueries({ queryKey: ["metal", "cutting-rates"] });
				setDirty(false);
				setSaved(true);
				setTimeout(() => setSaved(false), 2000);
			} catch {
				// grpc interceptor shows toast
			} finally {
				setSaving(false);
			}
		}, 1200);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [dirty, drafts, initialized, qc]);

	function emptyDraft(): RateDraft {
		return {
			key: crypto.randomUUID(),
			shape: "_all",
			ratePerCutRub: "",
			ratePerMetreRub: "",
			kerfMm: "3",
		};
	}

	function updateDraft(key: string, patch: Partial<RateDraft>) {
		setDrafts((prev) =>
			prev.map((d) => (d.key === key ? { ...d, ...patch } : d)),
		);
		setDirty(true);
	}

	function addDraft() {
		setDrafts((prev) => [...prev, emptyDraft()]);
		setDirty(true);
	}

	function removeDraft(key: string) {
		setDrafts((prev) => prev.filter((d) => d.key !== key));
		setDirty(true);
	}

	if (isLoading) return <Skeleton className="h-48" />;

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					{t("metal_cutting_desc")}
				</p>
				<SaveIndicator saving={saving} saved={saved} />
			</div>

			{drafts.length === 0 ? (
				<Card className="flex flex-col items-center justify-center py-12 text-center">
					<Scissors className="h-10 w-10 text-muted-foreground/40 mb-3" />
					<p className="text-sm font-medium">
						{t("metal_cutting_empty_title")}
					</p>
					<p className="text-xs text-muted-foreground mt-1 mb-4 max-w-sm">
						{t("metal_cutting_empty_desc")}
					</p>
					<Button size="sm" onClick={addDraft}>
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						{t("metal_add_rate")}
					</Button>
				</Card>
			) : (
				<>
					<div className="rounded-lg border overflow-hidden">
						<Table>
							<TableHeader>
								<TableRow className="bg-muted/30">
									<TableHead className="w-[200px]">
										{t("metal_col_profile")}
									</TableHead>
									<TableHead>{t("metal_col_per_cut")}</TableHead>
									<TableHead>{t("metal_col_per_metre")}</TableHead>
									<TableHead className="w-[100px]">
										{t("metal_col_kerf")}
									</TableHead>
									<TableHead className="w-[50px]" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{drafts.map((d) => (
									<TableRow key={d.key}>
										<TableCell>
											<Select
												value={d.shape}
												onValueChange={(v) => updateDraft(d.key, { shape: v })}
											>
												<SelectTrigger className="h-8 w-full">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													{SHAPE_OPTIONS.map((o) => (
														<SelectItem key={o.value} value={o.value}>
															{o.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</TableCell>
										<TableCell>
											<Input
												className="h-8 w-28"
												type="number"
												min="0"
												step="0.01"
												placeholder="0.00"
												value={d.ratePerCutRub}
												onChange={(e) =>
													updateDraft(d.key, { ratePerCutRub: e.target.value })
												}
											/>
										</TableCell>
										<TableCell>
											<Input
												className="h-8 w-28"
												type="number"
												min="0"
												step="0.01"
												placeholder="0.00"
												value={d.ratePerMetreRub}
												onChange={(e) =>
													updateDraft(d.key, {
														ratePerMetreRub: e.target.value,
													})
												}
											/>
										</TableCell>
										<TableCell>
											<Input
												className="h-8 w-20"
												type="number"
												min="0"
												step="0.5"
												value={d.kerfMm}
												onChange={(e) =>
													updateDraft(d.key, { kerfMm: e.target.value })
												}
											/>
										</TableCell>
										<TableCell>
											<Button
												variant="ghost"
												size="icon"
												className="h-8 w-8"
												onClick={() => removeDraft(d.key)}
											>
												<Trash2 className="h-3.5 w-3.5" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
					<Button variant="outline" size="sm" onClick={addDraft}>
						<Plus className="h-3.5 w-3.5 mr-1.5" />
						{t("metal_add_rate")}
					</Button>
				</>
			)}
		</div>
	);
}

// ── Sortament ────────────────────────────────────────────────────

function useSortamentShapes() {
	const { t } = useTranslation("settings");
	return [
		{ value: "9", label: t("metal_shape_rebar") },
		{ value: "5", label: t("metal_shape_round_pipe") },
		{ value: "11", label: t("metal_shape_l_angle") },
		{ value: "8", label: t("metal_sortament_sheet_label") },
	];
}

function useDimLabels() {
	const { t } = useTranslation("settings");
	return {
		diameter: t("metal_dim_label_diameter"),
		outer_diameter: t("metal_dim_label_outer_diameter"),
		wall_thickness: t("metal_dim_label_wall_thickness"),
		shelf: t("metal_dim_label_shelf"),
		thickness: t("metal_dim_label_thickness"),
	} as Record<string, string>;
}

function SortamentTab() {
	const { t } = useTranslation("settings");
	const SORTAMENT_SHAPES = useSortamentShapes();
	const DIM_LABELS = useDimLabels();
	const [shape, setShape] = useState("9");
	const [search, setSearch] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["metal", "sortament", shape],
		queryFn: () => client.getSortament({ shape: Number.parseInt(shape) }),
	});

	const filtered = useMemo(() => {
		if (!data?.entries) return [];
		if (!search) return data.entries;
		const q = search.toLowerCase();
		return data.entries.filter((e) => {
			const dims = e.dimensions?.toJson() as Record<string, number> | undefined;
			if (!dims) return false;
			return (
				Object.values(dims).some((v) => String(v).includes(q)) ||
				e.gost.toLowerCase().includes(q)
			);
		});
	}, [data, search]);

	return (
		<div className="space-y-4">
			<p className="text-sm text-muted-foreground">
				{t("metal_sortament_desc")}
			</p>
			<div className="flex items-center gap-3">
				<Select
					value={shape}
					onValueChange={(v) => {
						setShape(v);
						setSearch("");
					}}
				>
					<SelectTrigger className="w-48 h-8">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{SORTAMENT_SHAPES.map((s) => (
							<SelectItem key={s.value} value={s.value}>
								{s.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<div className="relative flex-1 max-w-xs">
					<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						className="h-8 pl-8"
						placeholder={t("metal_sortament_search")}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>
				{data?.entries && (
					<Badge variant="secondary" className="text-xs">
						{t("metal_sortament_count", {
							filtered: filtered.length,
							total: data.entries.length,
						})}
					</Badge>
				)}
			</div>
			{isLoading ? (
				<Skeleton className="h-48" />
			) : (
				<div className="rounded-lg border overflow-hidden">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/30">
								<TableHead>{t("metal_col_dimensions")}</TableHead>
								<TableHead className="text-right">
									{t("metal_col_mass")}
								</TableHead>
								<TableHead className="text-right">
									{t("metal_col_area")}
								</TableHead>
								<TableHead>{t("metal_col_gost")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{filtered.map((e, i) => {
								const dims = e.dimensions?.toJson() as
									| Record<string, number>
									| undefined;
								const dimsStr = dims
									? Object.entries(dims)
											.map(([k, v]) => `${DIM_LABELS[k] ?? k} ${v}`)
											.join(" × ")
									: "—";
								return (
									<TableRow key={i}>
										<TableCell className="font-medium font-mono">
											{dimsStr}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{e.massPerM.toFixed(3)}
										</TableCell>
										<TableCell className="text-right tabular-nums">
											{e.areaCm2 > 0 ? e.areaCm2.toFixed(2) : "—"}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{e.gost}
										</TableCell>
									</TableRow>
								);
							})}
							{filtered.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={4}
										className="text-center text-muted-foreground py-8"
									>
										{search
											? t("metal_sortament_not_found")
											: t("metal_sortament_no_data")}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}

// ── Calculator ───────────────────────────────────────────────────

function CalculatorTab() {
	const { t } = useTranslation("settings");
	const CALC_SHAPES = useCalcShapes();
	const SHAPE_DIMS = useShapeDims();
	const METALS = useMetals();
	const [shape, setShape] = useState("9");
	const [dims, setDims] = useState<Record<string, string>>({ diameter: "12" });
	const [lengthMm, setLengthMm] = useState("11700");
	const [metal, setMetal] = useState("steel");
	const [mode, setMode] = useState<"weight" | "length">("weight");
	const [weightKg, setWeightKg] = useState("1000");
	const [result, setResult] = useState<{
		value: number;
		unit: string;
		ld: number;
		tolMin: number;
		tolMax: number;
	} | null>(null);
	const [loading, setLoading] = useState(false);

	const dimFields = SHAPE_DIMS[shape] ?? [];

	async function calculate() {
		setLoading(true);
		try {
			const parsed: Record<string, number> = {};
			for (const f of dimFields) {
				const v = Number.parseFloat(dims[f.key] || "0");
				if (v > 0) parsed[f.key] = v;
			}
			const { Struct } = await import("@bufbuild/protobuf");
			const goal =
				mode === "weight"
					? { case: "lengthMm" as const, value: Number.parseFloat(lengthMm) }
					: { case: "weightKg" as const, value: Number.parseFloat(weightKg) };
			const resp = await client.calculateWeight({
				shape: Number.parseInt(shape),
				dimensions: Struct.fromJson(parsed),
				metal,
				goal,
			});
			setResult({
				value: resp.resultValue,
				unit: resp.resultUnit,
				ld: resp.linearDensityKgPerM,
				tolMin: resp.toleranceMin,
				tolMax: resp.toleranceMax,
			});
		} catch {
			/* grpc interceptor */
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="space-y-4 max-w-lg">
			<p className="text-sm text-muted-foreground">{t("metal_calc_desc")}</p>
			<div className="grid gap-3">
				<div className="grid grid-cols-2 gap-3">
					<div className="space-y-1.5">
						<Label className="text-sm">{t("metal_calc_profile")}</Label>
						<Select
							value={shape}
							onValueChange={(v) => {
								setShape(v);
								setDims({});
								setResult(null);
							}}
						>
							<SelectTrigger className="h-9">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{CALC_SHAPES.map((s) => (
									<SelectItem key={s.value} value={s.value}>
										{s.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">{t("metal_calc_metal")}</Label>
						<Select value={metal} onValueChange={setMetal}>
							<SelectTrigger className="h-9">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{METALS.map((m) => (
									<SelectItem key={m.value} value={m.value}>
										{m.label} ({m.density})
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<div
					className={cn(
						"grid gap-3",
						dimFields.length <= 2 ? "grid-cols-2" : "grid-cols-3",
					)}
				>
					{dimFields.map((f) => (
						<div key={f.key} className="space-y-1.5">
							<Label className="text-sm">{f.label}</Label>
							<Input
								className="h-9"
								type="number"
								min="0"
								step="0.1"
								placeholder="0"
								value={dims[f.key] ?? ""}
								onChange={(e) =>
									setDims((p) => ({ ...p, [f.key]: e.target.value }))
								}
							/>
						</div>
					))}
				</div>
				<div className="flex gap-3 items-end">
					<div className="space-y-1.5 flex-1">
						<Label className="text-sm">{t("metal_calc_mode")}</Label>
						<Select
							value={mode}
							onValueChange={(v) => setMode(v as "weight" | "length")}
						>
							<SelectTrigger className="h-9">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="weight">
									{t("metal_calc_weight_by_length")}
								</SelectItem>
								<SelectItem value="length">
									{t("metal_calc_length_by_weight")}
								</SelectItem>
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5 flex-1">
						<Label className="text-sm">
							{mode === "weight"
								? t("metal_calc_length_mm")
								: t("metal_calc_weight_kg")}
						</Label>
						<Input
							className="h-9"
							type="number"
							min="0"
							value={mode === "weight" ? lengthMm : weightKg}
							onChange={(e) =>
								mode === "weight"
									? setLengthMm(e.target.value)
									: setWeightKg(e.target.value)
							}
						/>
					</div>
				</div>
				<Button className="w-full" onClick={calculate} disabled={loading}>
					{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
					{t("metal_calc_button")}
				</Button>
			</div>
			{result && (
				<Card className="px-4 py-3">
					<div className="grid grid-cols-3 gap-4">
						<div>
							<p className="text-xs text-muted-foreground">
								{result.unit === "kg"
									? t("metal_calc_result_mass")
									: t("metal_calc_result_length")}
							</p>
							<p className="text-xl font-semibold tabular-nums">
								{result.value.toFixed(2)}{" "}
								<span className="text-sm font-normal text-muted-foreground">
									{result.unit === "kg"
										? t("metal_unit_kg")
										: t("metal_unit_mm")}
								</span>
							</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">
								{t("metal_calc_linear_density")}
							</p>
							<p className="text-xl font-semibold tabular-nums">
								{result.ld.toFixed(3)}{" "}
								<span className="text-sm font-normal text-muted-foreground">
									{t("metal_unit_kg_per_m")}
								</span>
							</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">
								{t("metal_calc_tolerance")}
							</p>
							<p className="text-sm tabular-nums text-muted-foreground mt-1">
								{result.tolMin.toFixed(2)} — {result.tolMax.toFixed(2)}
							</p>
						</div>
					</div>
					{result.unit === "kg" && result.ld > 0 && (
						<div className="mt-3 pt-3 border-t text-xs text-muted-foreground space-y-0.5">
							<p>
								{t("metal_calc_1t_equals")}{" "}
								<b className="text-foreground">
									{(1000 / result.ld).toFixed(1)}
								</b>{" "}
								{t("metal_unit_m")}
							</p>
							<p>
								{t("metal_calc_1m_equals")}{" "}
								<b className="text-foreground">{result.ld.toFixed(3)}</b>{" "}
								{t("metal_unit_kg")}
							</p>
						</div>
					)}
				</Card>
			)}
		</div>
	);
}

// ── Price converter ──────────────────────────────────────────────

function usePriceUnits() {
	const { t } = useTranslation("settings");
	return [
		{
			value: "tonne",
			label: t("metal_price_per_tonne"),
			proto: MetalPriceUnit.PER_TONNE,
		},
		{
			value: "kg",
			label: t("metal_price_per_kg"),
			proto: MetalPriceUnit.PER_KG,
		},
		{
			value: "metre",
			label: t("metal_price_per_metre"),
			proto: MetalPriceUnit.PER_METRE,
		},
		{
			value: "piece",
			label: t("metal_price_per_piece"),
			proto: MetalPriceUnit.PER_PIECE,
		},
		{
			value: "m2",
			label: t("metal_price_per_m2"),
			proto: MetalPriceUnit.PER_M2,
		},
	];
}

function PriceConverterTab() {
	const { t } = useTranslation("settings");
	const PRICE_UNITS = usePriceUnits();
	const [price, setPrice] = useState("50000");
	const [fromUnit, setFromUnit] = useState("tonne");
	const [toUnit, setToUnit] = useState("metre");
	const [ld, setLd] = useState("0.888");
	const [wpp, setWpp] = useState("10.39");
	const [wpm2, setWpm2] = useState("");
	const [result, setResult] = useState<{
		cents: number;
		factor: number;
	} | null>(null);
	const [loading, setLoading] = useState(false);

	async function convert() {
		setLoading(true);
		try {
			const from = PRICE_UNITS.find((u) => u.value === fromUnit)!;
			const to = PRICE_UNITS.find((u) => u.value === toUnit)!;
			const resp = await client.convertPrice({
				valueCents: BigInt(Math.round(Number.parseFloat(price) * 100)),
				fromUnit: from.proto,
				toUnit: to.proto,
				linearDensityKgM: Number.parseFloat(ld) || 0,
				weightPerPieceKg: Number.parseFloat(wpp) || 0,
				weightPerM2Kg: Number.parseFloat(wpm2) || 0,
			});
			setResult({ cents: Number(resp.convertedCents), factor: resp.factor });
		} catch {
			/* grpc interceptor */
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="space-y-4 max-w-lg">
			<p className="text-sm text-muted-foreground">
				{t("metal_converter_desc")}
			</p>
			<div className="grid gap-3">
				<div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
					<div className="space-y-1.5">
						<Label className="text-sm">{t("metal_converter_price")}</Label>
						<Input
							className="h-9"
							type="number"
							value={price}
							onChange={(e) => setPrice(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">{t("metal_converter_from")}</Label>
						<Select value={fromUnit} onValueChange={setFromUnit}>
							<SelectTrigger className="h-9 w-36">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PRICE_UNITS.map((u) => (
									<SelectItem key={u.value} value={u.value}>
										{u.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">{t("metal_converter_to")}</Label>
						<Select value={toUnit} onValueChange={setToUnit}>
							<SelectTrigger className="h-9 w-36">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PRICE_UNITS.map((u) => (
									<SelectItem key={u.value} value={u.value}>
										{u.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="grid grid-cols-3 gap-3">
					<div className="space-y-1.5">
						<Label className="text-sm">
							{t("metal_converter_linear_density")}
						</Label>
						<Input
							className="h-9"
							type="number"
							step="0.001"
							value={ld}
							onChange={(e) => setLd(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">
							{t("metal_converter_piece_weight")}
						</Label>
						<Input
							className="h-9"
							type="number"
							step="0.01"
							value={wpp}
							onChange={(e) => setWpp(e.target.value)}
						/>
					</div>
					<div className="space-y-1.5">
						<Label className="text-sm">{t("metal_converter_m2_weight")}</Label>
						<Input
							className="h-9"
							type="number"
							step="0.01"
							placeholder="—"
							value={wpm2}
							onChange={(e) => setWpm2(e.target.value)}
						/>
					</div>
				</div>
				<Button className="w-full" onClick={convert} disabled={loading}>
					{loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
					{t("metal_converter_button")}
				</Button>
			</div>
			{result && (
				<Card className="px-4 py-3">
					<div className="grid grid-cols-2 gap-4">
						<div>
							<p className="text-xs text-muted-foreground">
								{t("metal_converter_result")}
							</p>
							<p className="text-xl font-semibold tabular-nums">
								{(result.cents / 100).toFixed(2)}{" "}
								<span className="text-sm font-normal text-muted-foreground">
									₽ {PRICE_UNITS.find((u) => u.value === toUnit)?.label}
								</span>
							</p>
						</div>
						<div>
							<p className="text-xs text-muted-foreground">
								{t("metal_converter_factor")}
							</p>
							<p className="text-xl font-semibold tabular-nums">
								x{result.factor.toFixed(6)}
							</p>
						</div>
					</div>
				</Card>
			)}
		</div>
	);
}
